import BaseObject from 'app/src/base/BaseObject';
import SettingsRouter from 'app/router/SettingsRouter';
import AppViewManager from 'app/AppViewManager';
import PaymentServiceDegradationViewController from 'app/src/controllers/payments/PaymentServiceDegradationViewController';
import PaymentProcessingViewController from 'app/src/controllers/settings/paymentProcessing/PaymentProcessingViewController';
import GatewayDetailViewController from 'app/src/controllers/settings/paymentProcessing/GatewayDetailViewController';
import TerminalDetailViewController from 'app/src/controllers/settings/paymentProcessing/TerminalDetailViewController';
import GatewayConfigCollection from 'app/src/data/models/GatewayConfigCollection';
import GatewayConfig from 'app/src/data/models/GatewayConfig';
import Gateway from 'app/src/data/models/Gateway';
import CCTerminalCollection from 'app/src/data/models/CCTerminalCollection';
import CCTerminal from 'app/src/data/models/CCTerminal';
import Localize from 'app/src/localize/Localize';
import CommonData from 'app/src/data/CommonData';
import PaymentServiceAlert from 'app/src/controllers/payments/PaymentServiceAlert';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import Monitoring from 'shared_modules/Monitoring';

import $ from 'jquery';
import get from 'lodash/get';

export default class SettingsController extends BaseObject {
  constructor() {
    super();

    this._appViewManager = AppViewManager.getInstance();

    this._router = new SettingsRouter({ controller: this });
  }

  /**
   * When the Payment Processing page is visited, set the Breadcrumbs appropriately.
   * Enact the View Controller which will populate the page with the landing.
   */
  showPaymentProcessing() {
    showNotifier('working');

    let gatewayConfigs = new GatewayConfigCollection();
    let ccTerminals = new CCTerminalCollection();

    /**
     * TODO: 'setCrumbs/crumbItem'
     * From the Global Namespace. Eventually we will need to reverse engineer the show/hide logic and
     * absorb the responsibility.
     */
    setCrumbs([crumbItem(Localize.translate('Payment Processing'))]);

    $.when(
      gatewayConfigs.fetch(),
      ccTerminals.fetch(),
      PaymentTypeManager.init() // re-init to fetch the latest payment types (in case they've changed)
    )
      .done(() => {
        this._appViewManager.setMainView(
          new PaymentProcessingViewController(gatewayConfigs, ccTerminals)
        );
      })
      .fail(fetchPromisesFailure => {
        const reason = get(fetchPromisesFailure, 'responseJSON.message', null);
        Monitoring.NRError(
          new Error(`Unable to show the payment processing settings section`),
          'SettingsController',
          { reason }
        );
        this._appViewManager.setMainView(new PaymentServiceDegradationViewController());
      })
      .always(() => {
        hideNotifier('working');
      });
  }

  /**
   * When the AddGateway button is clicked or the page is visited.
   * This is the navigation logic to the page where you can Add Gateways.
   */
  addGateway() {
    showNotifier('working');
    /**
     * This requires further investigation to make work right with the trailing Breadcrumb
     */
    setCrumbs([crumbItem(Localize.translate('Add a Payment Gateway'))]);
    let gatewayConfig = new GatewayConfig();
    let gateway = new Gateway();

    $.when(
      gateway.fetch(),

      // TODO: Until we have a solid Push Notification strategy for handling changes
      // to the common data like shops and registers, we're forced to always fetch
      // the latest shop/register data from the server before we attempt to use it
      // in the GatewayDetailViewController
      CommonData.init()
    )
      .done(() => {
        if (!Array.isArray(gateway.get('gateway'))) {
          gateway.set({
            gateway: [gateway.get('gateway')],
          });
        }
        this._appViewManager.setMainView(new GatewayDetailViewController(gatewayConfig, gateway));
      })
      .fail(jqXHR => {
        PaymentServiceAlert.alert(jqXHR);
      })
      .always(() => {
        hideNotifier('working');
      });
  }

  /**
   * Route handler for when Edit Gateway link is clicked or page is visited
   *
   * @param gatewayConfigID {string|number}  The ID of the gateway to edit.
   */
  editGateway(gatewayConfigID) {
    showNotifier('working');
    setCrumbs([crumbItem(Localize.translate('Edit a Payment Gateway'))]);

    let gatewayConfig = new GatewayConfig({ gatewayConfigID });
    let gateway = new Gateway();

    $.when(
      gateway.fetch(),
      gatewayConfig.fetch(),

      // TODO: Until we have a solid Push Notification strategy for handling changes
      // to the common data like shops and registers, we're forced to always fetch
      // the latest shop/register data from the server before we attempt to use it
      // in the GatewayDetailViewController
      CommonData.init()
    )
      .done(() => {
        if (!Array.isArray(gateway.get('gateway'))) {
          gateway.set({
            gateway: [gateway.get('gateway')],
          });
        }
        this._appViewManager.setMainView(new GatewayDetailViewController(gatewayConfig, gateway));
      })
      .fail(jqXHR => {
        PaymentServiceAlert.alert(jqXHR);
      })
      .always(() => {
        hideNotifier('working');
      });
  }

  /**
   * Route handler for when an add terminal link is clicked or the page is visited.
   *
   * @param gatewayConfigID {string|number}   The ID of the gateway to which the terminal is
   *                                          to be associated.
   */
  addTerminal(gatewayConfigID) {
    showNotifier('working');

    let gatewayConfig = new GatewayConfig({
      gatewayConfigID,
    });

    gatewayConfig
      .fetch()
      .done(() => {
        setCrumbs([crumbItem(Localize.translate('Payment Processing / Add a Payment Gateway'))]);
        let ccTerminal = new CCTerminal({
          gatewayConfigID: gatewayConfig.id,
        });
        switch (gatewayConfig.get('gatewayID')) {
          case GatewayConfig.GatewayType.CAYAN:
            ccTerminal.set('type', CCTerminal.TerminalType.GENIUS);
            break;

          case GatewayConfig.GatewayType.LIGHTSPEED_PAYMENTS:
            ccTerminal.set('type', CCTerminal.TerminalType.LIGHTSPEED);
            break;

          case GatewayConfig.GatewayType.FIRST_DATA:
          case GatewayConfig.GatewayType.VANTIV_CANADA:
          case GatewayConfig.GatewayType.VANTIV_US:
            ccTerminal.set('type', CCTerminal.TerminalType.BLACKLINE);
            break;

          case GatewayConfig.GatewayType.VANTIV_TRIPOS:
            ccTerminal.set('type', CCTerminal.TerminalType.TRIPOS);
            break;
        }

        this._appViewManager.setMainView(
          new TerminalDetailViewController(ccTerminal, gatewayConfig)
        );
      })
      .always(() => {
        hideNotifier('working');
      });
  }

  /**
   * Route handler for when an edit terminal link is clicked or the page is visited.
   *
   * @param terminalID {string|number}    The ID of the terminal to be displayed.
   */
  editTerminal(terminalID) {
    showNotifier('working');

    let ccTerminal = new CCTerminal({
      ccTerminalID: terminalID,
    });

    ccTerminal
      .fetch()
      .done(() => {
        this._appViewManager.setMainView(new TerminalDetailViewController(ccTerminal));
      })
      .fail(jqXHR => {
        PaymentServiceAlert.alert(jqXHR);
      })
      .always(() => {
        hideNotifier('working');
      });
  }
}
